import {DomainObject, oneOf} from 'shared/store';
import { observable, computed, makeObservable } from 'mobx';
import {daysFromNow, calendarDate} from 'shared/tools';
import {absoluteTimestampDate} from 'shared/tools/dates';

class NmbrPayroll extends DomainObject {
  status;
  approvalDueDate;
  nextPayrollApprovalDueDate;
  payDate;
  type;

  _relations = {
    status: oneOf(['draft', 'approved', 'paid', 'failed', 'partially_paid', 'processing']),
    type: oneOf(['regular', 'off_cycle', 'historical'])
  };

  constructor(other) {
    super();

    makeObservable(this, {
      status: observable,
      approvalDueDate: observable,
      nextPayrollApprovalDueDate: observable,
      payDate: observable,
      type: observable,
      approvalDueDateView: computed,
      approvalDueDateDaysFromNow: computed,
      nextPayrollApprovalDueDateView: computed,
      nextPayrollApprovalDueDateDaysFromNow: computed,
      payDateView: computed,
      payDateDaysFromNow: computed,
      payrollStatusColor: computed,
      payrollDates: computed,
      approvalDueDateColour: computed
    });

    this.merge(other);
  }

  get approvalDueDateView() {
    return absoluteTimestampDate(this.approvalDueDate, {includeDayOfWeek: true});
  }

  get approvalDueDateDaysFromNow() {
    return daysFromNow(this.approvalDueDate);
  }

  get nextPayrollApprovalDueDateView() {
    return calendarDate(this.nextPayrollApprovalDueDate);
  }

  get nextPayrollApprovalDueDateDaysFromNow() {
    return daysFromNow(this.nextPayrollApprovalDueDate);
  }

  get payDateView() {
    return calendarDate(this.payDate);
  }

  get payDateDaysFromNow() {
    return daysFromNow(this.payDate);
  }

  get payrollStatusColor() {
    switch (this.status) {
      case 'approved':
      case 'paid':
      case 'processing':
      case 'partially_paid':
        return 'grass';
      case 'draft':
        return 'turquoise';
      case 'failed':
        return 'scarlet';
      default:
        throw new Error(`Status ${this.status} is not supported.`);
    }
  }

  get payrollDates() {
    const datesArray = [];

    if (this.status === 'draft') {
      datesArray.push({
        dateName: 'Payroll Approval Due Date',
        date: this.approvalDueDateView,
        daysFromNow: this.approvalDueDateDaysFromNow,
        colour: this.approvalDueDateColour
      });
    }

    datesArray.push({
      dateName: 'Pay Date',
      date: this.payDateView,
      daysFromNow: this.payDateDaysFromNow
    });

    if (this.status !== 'draft' && this.nextPayrollApprovalDueDate) {
      datesArray.push({
        dateName: 'Next Payroll Approval Due Date',
        date: this.nextPayrollApprovalDueDateView,
        daysFromNow: this.nextPayrollApprovalDueDateDaysFromNow,
        colour: this.approvalDueDateColour
      });
    }

    return datesArray;
  }

  get approvalDueDateColour() {
    if (this.approvalDueDateDaysFromNow === 2) {
      return 'sun';
    } else if (this.approvalDueDateDaysFromNow <= 1) {
      return 'scarlet';
    }

    return null;
  }
}

export default NmbrPayroll;
